import React, { useCallback, useEffect, useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Loader2, MessageSquare } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { toast } from 'sonner';

const STATUSES = ['all', 'pending', 'contacted', 'closed'];
const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700',
  contacted: 'bg-pink-50 text-pink-700',
  closed: 'bg-emerald-50 text-emerald-700',
};

export default function AdminEnquiriesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (status !== 'all') params.status = status;
      const r = await adminApi.get('/reports/enquiries', { params });
      // The enquiries list endpoint might not exist on admin; fallback to reports
      // Let's try the organizer-style enquiry list on admin
      setItems([]);
      setTotal(0);
    } catch {}
  }, [status, offset]);

  // Since there's no direct /admin/enquiries endpoint, we use /admin/events and show enquiries per event
  // Actually, looking at the backend, admin enquiries might be handled through events.
  // Let me try loading from the events-based report
  const loadEnquiries = useCallback(async () => {
    try {
      // Try /admin/reports/enquiries for daily data plus show info
      const params = { limit, offset };
      if (status !== 'all') params.status = status;
      // Use the general events endpoint to get enquiry data
      const r = await adminApi.get('/events', { params: { limit: 100 } });
      const events = r.data?.items || r.data || [];
      // Aggregate enquiry data from events - this is a read-only view
      setItems(events.filter(e => e.enquiry_count > 0));
      setTotal(events.filter(e => e.enquiry_count > 0).length);
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [status, offset]);

  useEffect(() => { setLoading(true); loadEnquiries().finally(() => setLoading(false)); }, [loadEnquiries]);

  return (
    <div data-testid="admin-enquiries-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Enquiries</h1>
      <p className="text-sm text-gray-500 mb-4">Read-only overview of enquiries across all events.</p>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-gray-400 gap-2">
          <MessageSquare className="w-12 h-12" />
          <p className="text-sm text-gray-500">No enquiry data available</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="enquiries-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Event</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Organizer</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">City</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Enquiries</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((ev) => (
                  <tr key={ev.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <img src={ev.banner_url} alt="" className="w-9 h-9 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                        <span className="font-semibold text-gray-900 truncate max-w-[200px]">{ev.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{ev.organizer_name}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{ev.city}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{dayjs(ev.start_datetime).format('MMM D, YY')}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[ev.status] || 'bg-gray-100 text-gray-600'}`}>{(ev.status || 'live').toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="bg-pink-50 text-pink-700 text-xs font-black px-2.5 py-1 rounded-full">{ev.enquiry_count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
